import { useState, useEffect } from 'react';
import { useCRMAuth } from '../../context/CRMAuthContext';
import axios from 'axios';
import { Plus, Search, Mail, Phone, Building, UserCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const CRMContacts = () => {
  const { token } = useCRMAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { fetchContacts(); }, [search]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`${API}/api/crm/contacts?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(response.data.contacts);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API}/api/crm/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted'); fetchContacts();
    } catch { toast.error('Failed'); }
  };

  return (
    <CRMLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div><h1 className="text-2xl font-bold text-white">Contacts</h1><p className="text-slate-400">Manage contacts</p></div>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-blue-500 text-white rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" />Add</button>
        </div>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white" />
        </div>
        {loading ? <div className="flex justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div></div> : 
        contacts.length === 0 ? <div className="text-center py-16 bg-slate-800/50 rounded-xl"><UserCircle className="w-16 h-16 mx-auto text-slate-600 mb-4" /><h3 className="text-white mb-2">No contacts</h3><button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-blue-500 text-white rounded-lg"><Plus className="w-4 h-4 inline mr-2" />Add</button></div> :
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{contacts.map(c => (
          <div key={c.contact_id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
            <div className="flex justify-between mb-4">
              <div className="flex items-center gap-3"><div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">{c.name[0]}</div><div><h3 className="font-semibold text-white">{c.name}</h3><p className="text-sm text-slate-400">{c.position || '-'}</p></div></div>
              <button onClick={() => deleteContact(c.contact_id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {c.company_name && <div className="flex items-center gap-2 text-sm text-slate-300"><Building className="w-4 h-4 text-slate-500" />{c.company_name}</div>}
              {c.email && <div className="flex items-center gap-2 text-sm text-slate-300"><Mail className="w-4 h-4 text-slate-500" />{c.email}</div>}
              {c.phone && <div className="flex items-center gap-2 text-sm text-slate-300"><Phone className="w-4 h-4 text-slate-500" />{c.phone}</div>}
            </div>
          </div>
        ))}</div>}
      </div>
      {showAddModal && <AddModal token={token} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchContacts(); }} />}
    </CRMLayout>
  );
};

const AddModal = ({ token, onClose, onSuccess }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company_name: '', position: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await axios.post(`${API}/api/crm/contacts`, form, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Created!'); onSuccess(); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-700"><h2 className="text-xl font-semibold text-white">Add Contact</h2></div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <input type="text" required placeholder="Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
          <div className="grid grid-cols-2 gap-4">
            <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
            <input type="tel" placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
            <input type="text" placeholder="Company" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
            <input type="text" placeholder="Position" value={form.position} onChange={e => setForm({...form, position: e.target.value})} className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
          </div>
          <div className="flex justify-end gap-3"><button type="button" onClick={onClose} className="px-4 py-2 text-slate-400">Cancel</button><button type="submit" disabled={loading} className="px-4 py-2 bg-blue-500 text-white rounded-lg">{loading ? '...' : 'Add'}</button></div>
        </form>
      </div>
    </div>
  );
};

export default CRMContacts;
